import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { IEditProfileDialogProps } from '@/@types/serviceTypes';
import type { Id } from '@convex/_generated/dataModel';

const TEMPERATURE_OPTIONS = [
  { value: 'hot', label: 'Hot' },
  { value: 'warm', label: 'Warm' },
  { value: 'cold', label: 'Cold' },
] as const; // AS_JUSTIFIED: const assertion for literal types from static options

const LEAD_TYPE_OPTIONS = [
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'investor', label: 'Investor' },
  { value: 'renter', label: 'Renter' },
  { value: 'landlord', label: 'Landlord' },
] as const; // AS_JUSTIFIED: const assertion for literal types from static options

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'quote_sent', label: 'Quote Sent' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'appointment_booked', label: 'Appointment Booked' },
  { value: 'showing_scheduled', label: 'Showing Scheduled' },
  { value: 'offer_made', label: 'Offer Made' },
  { value: 'under_contract', label: 'Under Contract' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'nurture', label: 'Nurture' },
] as const; // AS_JUSTIFIED: const assertion for literal types from static options

const SOURCE_OPTIONS = [
  { value: 'website', label: 'Website' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'email', label: 'Email' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'organic', label: 'Organic' },
  { value: 'ad', label: 'Ad' },
  { value: 'referral', label: 'Referral' },
] as const; // AS_JUSTIFIED: const assertion for literal types from static options

type TTemperature = (typeof TEMPERATURE_OPTIONS)[number]['value']; // LOCAL_TYPE_JUSTIFIED: derived from const array, internal to dialog
type TLeadType = (typeof LEAD_TYPE_OPTIONS)[number]['value']; // LOCAL_TYPE_JUSTIFIED: derived from const array, internal to dialog
type TStatus = (typeof STATUS_OPTIONS)[number]['value']; // LOCAL_TYPE_JUSTIFIED: derived from const array, internal to dialog
type TSource = (typeof SOURCE_OPTIONS)[number]['value']; // LOCAL_TYPE_JUSTIFIED: derived from const array, internal to dialog

export function EditProfileDialog({
  open,
  onOpenChange,
  leadId,
  name,
  temperature,
  leadType,
  status,
  source,
}: IEditProfileDialogProps) {
  const updateContactInfo = useMutation(api.leads.updateContactInfo);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState(name || '');
  const [formTemp, setFormTemp] = useState(temperature || '');
  const [formType, setFormType] = useState(leadType || '');
  const [formStatus, setFormStatus] = useState(status);
  const [formSource, setFormSource] = useState(source || '');

  const resetForm = useCallback(() => {
    setFormName(name || '');
    setFormTemp(temperature || '');
    setFormType(leadType || '');
    setFormStatus(status);
    setFormSource(source || '');
  }, [name, temperature, leadType, status, source]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) resetForm();
      onOpenChange(next);
    },
    [onOpenChange, resetForm]
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const typedLeadId = leadId as Id<'leads'>; // AS_JUSTIFIED: leadId comes from props as string; Convex guarantees valid Id<"leads">
      await updateContactInfo({
        leadId: typedLeadId,
        ...(formName && formName !== name ? { name: formName } : {}),
        ...(formTemp ? { temperature: formTemp as TTemperature } : {}), // AS_JUSTIFIED: value from Select with fixed options
        ...(formType ? { leadType: formType as TLeadType } : {}), // AS_JUSTIFIED: value from Select with fixed options
        ...(formStatus !== status ? { status: formStatus as TStatus } : {}), // AS_JUSTIFIED: value from Select with fixed options
        ...(formSource ? { source: formSource as TSource } : {}), // AS_JUSTIFIED: value from Select with fixed options
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }, [
    formName,
    formTemp,
    formType,
    formStatus,
    formSource,
    leadId,
    name,
    status,
    updateContactInfo,
    onOpenChange,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-name">Name</Label>
            <Input id="edit-name" value={formName} onChange={e => setFormName(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Temperature</Label>
            <Select value={formTemp} onValueChange={setFormTemp}>
              <SelectTrigger>
                <SelectValue placeholder="Select temperature" />
              </SelectTrigger>
              <SelectContent>
                {TEMPERATURE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Lead Type</Label>
            <Select value={formType} onValueChange={setFormType}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {LEAD_TYPE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Status</Label>
            <Select value={formStatus} onValueChange={setFormStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Source</Label>
            <Select value={formSource} onValueChange={setFormSource}>
              <SelectTrigger>
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
